import Keycloak from 'keycloak-js';
import { ClientError, Status } from 'nice-grpc-web';

import type { AuthService, AuthSession } from './types';

const isInFrame = () => window.self !== window.top;
const unauthenticated = (details: string) => new ClientError('', Status.UNAUTHENTICATED, details);

export class KeycloakAuthService implements AuthService {
  private readonly keycloak: Keycloak;
  private initialized?: Promise<boolean>;

  constructor(url: string, realm: string, clientId: string) {
    this.keycloak = new Keycloak({ url, realm, clientId });
  }

  async check(): Promise<AuthSession | null> {
    // check-sso runs as a redirect of the page (no silent-check iframe); keycloak-js allows one init per page.
    this.initialized ??= this.keycloak.init({ onLoad: 'check-sso', pkceMethod: 'S256', checkLoginIframe: false });
    return (await this.initialized) ? { roles: this.keycloak.realmAccess?.roles ?? [] } : null;
  }

  login(): void {
    void this.keycloak.login();
  }

  retry(): void {
    window.location.reload();
  }

  inFrame(): boolean {
    return isInFrame();
  }

  async getToken(): Promise<string> {
    try {
      await this.keycloak.updateToken(30);
    } catch (error) {
      throw unauthenticated(`Token refresh failed: ${String(error)}`);
    }
    if (!this.keycloak.token) throw unauthenticated('No access token');
    return this.keycloak.token;
  }
}

export function rolesFromJwt(token: string): string[] {
  const payload = (token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
  const claims = JSON.parse(atob(payload)) as { realm_access?: { roles?: string[] } };
  return claims.realm_access?.roles ?? [];
}

/** Development builds only: a fixed token (e.g. from the notificationservice fake license service) instead of Keycloak. */
export class DevTokenAuthService implements AuthService {
  constructor(private readonly token: string) {}

  async check(): Promise<AuthSession> {
    return { roles: rolesFromJwt(this.token) };
  }

  login(): void {}

  retry(): void {
    window.location.reload();
  }

  inFrame(): boolean {
    return isInFrame();
  }

  async getToken(): Promise<string> {
    return this.token;
  }
}
